import React, { useState, useRef, useCallback } from 'react'
import { Upload, File, X, AlertCircle, Check } from 'lucide-react'
import { cn, formatFileSize } from '../../lib/utils'
import { useFileUpload } from '../../lib/hooks'
import toast from 'react-hot-toast'

interface FileUploadProps {
  onUpload: (file: File) => Promise<any>
  acceptedFileTypes?: string[]
  maxFileSize?: number
  multiple?: boolean
  disabled?: boolean
  className?: string
  children?: React.ReactNode
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onUpload,
  acceptedFileTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel.sheet.macroEnabled.12', 'image/tiff'],
  maxFileSize = 10 * 1024 * 1024, // 10MB
  multiple = false,
  disabled = false,
  className,
  children,
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { upload, progress, isUploading } = useFileUpload()

  const validateFile = (file: File): string | null => {
    if (!acceptedFileTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Please upload PDF, DOCX, or Excel files.`
    }
    
    if (file.size > maxFileSize) {
      return `File size ${formatFileSize(file.size)} exceeds maximum size of ${formatFileSize(maxFileSize)}.`
    }
    
    return null
  }

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files) return

    const fileArray = Array.from(files)
    const newErrors: string[] = []

    for (const file of fileArray) {
      const error = validateFile(file)
      if (error) {
        newErrors.push(error)
        continue
      }

      try {
        await upload(file, (formData, onProgress) => onUpload(file))
        setUploadedFiles(prev => [...prev, file])
        toast.success(`${file.name} uploaded successfully`)
      } catch (error) {
        newErrors.push(`Failed to upload ${file.name}: ${error}`)
      }
    }

    setErrors(newErrors)
  }, [upload, onUpload, maxFileSize, acceptedFileTypes])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    if (disabled || isUploading) return
    
    const files = e.dataTransfer.files
    handleFiles(files)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    handleFiles(files)
    
    // Clear the input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click()
    }
  }

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const clearErrors = () => {
    setErrors([])
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Upload Area */}
      <div
        className={cn(
          'file-upload-area',
          isDragOver && 'dragover',
          disabled && 'opacity-50 cursor-not-allowed',
          isUploading && 'pointer-events-none'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFileTypes.join(',')}
          multiple={multiple}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || isUploading}
        />

        {children || (
          <div className="group flex flex-col items-center justify-center gap-3 py-8">
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-full ring-4 ring-accent-50',
                'transition-transform duration-200 ease-out group-hover:-translate-y-0.5',
                isDragOver ? 'bg-accent-500 text-white' : 'bg-accent-100 text-accent-600'
              )}
            >
              <Upload className="w-5 h-5" />
            </div>
            <p className="text-sm font-medium text-gray-700">
              {isUploading ? 'Uploading…' : (
                <>
                  Drag and drop, or{' '}
                  <span className="text-accent-600 font-semibold group-hover:underline">browse</span>
                </>
              )}
            </p>
            <p className="text-[11px] text-gray-500">
              PDF, DOCX, and Excel files up to {formatFileSize(maxFileSize)}
            </p>
          </div>
        )}

        {/* Progress Bar */}
        {isUploading && (
          <div className="mt-4">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              {progress}% uploaded
            </p>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <h4 className="text-sm font-medium text-red-800">Upload Errors</h4>
            </div>
            <button
              onClick={clearErrors}
              className="text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="text-sm text-red-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files</h4>
          <div className="space-y-2">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md"
              >
                <div className="flex items-center">
                  <File className="w-4 h-4 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">{file.name}</p>
                    <p className="text-xs text-green-600">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <button
                    onClick={() => removeFile(index)}
                    className="text-green-600 hover:text-green-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Simple file input component
interface FileInputProps {
  onChange: (file: File | null) => void
  accept?: string
  placeholder?: string
  disabled?: boolean
  className?: string
}

export const FileInput: React.FC<FileInputProps> = ({
  onChange,
  accept = 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,image/tiff',
  placeholder = 'Choose file...',
  disabled = false,
  className,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setSelectedFile(file)
    onChange(file)
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const clearFile = () => {
    setSelectedFile(null)
    onChange(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className={cn('relative', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
      
      <div
        className={cn(
          'input cursor-pointer flex items-center justify-between',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={handleClick}
      >
        <span className={cn(
          'truncate',
          selectedFile ? 'text-foreground' : 'text-muted-foreground'
        )}>
          {selectedFile ? selectedFile.name : placeholder}
        </span>
        
        <div className="flex items-center space-x-2">
          {selectedFile && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                clearFile()
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <File className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  )
}

export default FileUpload